import { Tag } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import {
  CheckCircle,
  Clock,
  Info,
  MinusCircle,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';

/**
 * Chip trạng thái kèm icon — CHUẨN dùng chung cho mọi trạng thái ở Admin Web.
 *
 * <p>🔴 <b>Vì sao tự bọc flex thay vì dùng prop `icon` của antd:</b> prop `icon` nhắm tới icon-font
 * riêng của antd (`.anticon`), còn Phosphor render SVG thô nên antd không canh giữa/giãn cách đúng —
 * icon dính sát chữ, lệch dòng (thấy rõ khi phóng to bảng). Nên bọc `inline-flex` với `gap`.</p>
 *
 * <p>📌 <b>Icon mặc định chọn theo `color`</b> (tone chuẩn antd hoặc preset). Không cần truyền icon
 * cho từng chỗ — nhờ vậy mọi trạng thái đồng bộ: success luôn dấu tích, warning luôn tam giác…
 * Truyền `icon` để ghi đè khi một chip cần icon riêng (ví dụ "3 ca" dùng đồng hồ).</p>
 */

const TONE_ICON: Record<string, ReactNode> = {
  success: <CheckCircle size={12} weight="fill" aria-hidden />,
  green: <CheckCircle size={12} weight="fill" aria-hidden />,
  error: <XCircle size={12} weight="fill" aria-hidden />,
  red: <XCircle size={12} weight="fill" aria-hidden />,
  warning: <WarningCircle size={12} weight="fill" aria-hidden />,
  orange: <WarningCircle size={12} weight="fill" aria-hidden />,
  gold: <WarningCircle size={12} weight="fill" aria-hidden />,
  processing: <Clock size={12} aria-hidden />,
  blue: <Info size={12} aria-hidden />,
  // Tone trung tính (nháp, đã ngừng, đã thay thế, đã khóa) — một chấm mờ, không báo động.
  default: <MinusCircle size={12} aria-hidden />,
};

export interface StatusTagProps {
  /** Màu/tone của Tag — tone chuẩn antd (success/warning/error/default/processing) hoặc preset (blue/orange…). */
  color: string;
  /** Ghi đè icon mặc định. Bỏ trống thì tự chọn theo `color`. `null` để không hiện icon nào. */
  icon?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}

export function StatusTag({ color, icon, children, style }: StatusTagProps) {
  const resolvedIcon = icon === undefined ? TONE_ICON[color] : icon;
  return (
    <Tag
      color={color}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...style }}
    >
      {resolvedIcon}
      {children}
    </Tag>
  );
}
